import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import { api, ApiResponse } from "_/utils";
import { Uuid } from "_/types";

const SCOPE = "telemetry";

const KEYS = {
  file: (attemptId: Uuid) => [SCOPE, "file", attemptId],
};

/**
 * The single parquet file holding an attempt's telemetry records.
 *
 * Records are bundled once, after the print ends, so an attempt has exactly one
 * file for its whole duration.
 */
export type TelemetryFile = {
  /** Timestamp of the earliest record in the file, as an RFC 3339 string. */
  startAt: string;

  /** Timestamp of the latest record in the file, as an RFC 3339 string. */
  endAt: string;

  /** Blob holding the file's Parquet data. */
  blobId: Uuid;
};

/**
 * True when the file was refused because the attempt has not ended yet.
 *
 * Records are only bundled once a print finishes, so the server answers
 * `409 Conflict` rather than describing a partial dataset.
 */
export function isTelemetryNotReady(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 409;
}

/**
 * Whether an attempt's telemetry file is available, still being generated, or
 * absent because nothing was recorded.
 */
export type TelemetryFileState =
  | { state: "ready"; file: TelemetryFile }
  | { state: "generating"; pollAfterMs: number }
  | { state: "absent" };

/** Used when the server sends no usable `Retry-After`. */
const FALLBACK_POLL_INTERVAL = 5000;

/**
 * Poll delay the server asked for, in milliseconds.
 *
 * `Retry-After` is expressed in seconds. Only that numeric form is handled,
 * since it is what the telemetry route sends; the HTTP-date form falls back.
 */
export function pollAfterMsFrom(retryAfter: unknown): number {
  const seconds = Number(retryAfter);
  return Number.isFinite(seconds) && seconds > 0
    ? seconds * 1000
    : FALLBACK_POLL_INTERVAL;
}

/**
 * Retrieve the telemetry file recorded during an attempt.
 *
 * Authorized server side against `Telemetry::Read` on the domain owning the
 * attempt's machine, so any client-side check is only for the UI.
 */
export function useAttemptTelemetryFile(attemptId: Uuid) {
  return useQuery<TelemetryFileState>({
    queryKey: KEYS.file(attemptId),
    queryFn: async () => {
      try {
        const response = await api.get<ApiResponse<TelemetryFile | null>>(
          `/attempts/${attemptId}/telemetry`
        );

        // The server answers `202` while the aggregation job is queued or
        // running, with no file to describe yet.
        const file = response.status === 202 ? null : response.data.data;
        if (file) return { state: "ready", file };

        return {
          state: "generating",
          pollAfterMs: pollAfterMsFrom(response.headers["retry-after"]),
        };
      } catch (error) {
        // An attempt that recorded no telemetry is a state to render, not a
        // failure to report.
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return { state: "absent" };
        }
        throw error;
      }
    },
    staleTime: 1000 * 60,

    // Keep polling the same URL while generation is in flight, at the cadence
    // the `202` response's `Retry-After` asked for.
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.state === "generating" ? data.pollAfterMs : false;
    },

    // A print that is still running is a state, not a transient failure, so
    // retrying only delays showing the user why.
    retry: (failureCount, error) =>
      !isTelemetryNotReady(error) && failureCount < 3,

    // A refetch changes the object identity the database effect keys on, which
    // rebuilds it and discards the user's typed SQL and results.
    refetchOnWindowFocus: false,
  });
}
